import {
  Badge,
  Button,
  Card,
  Divider,
  Input,
  Spinner,
  Switch,
  Text,
  Title2,
  Title3,
  makeStyles,
} from '@fluentui/react-components';
import { complianceTones, complianceTokenFor, rawTokens, tokens, useColorScheme } from '@vigeo/ui';

/**
 * The living style guide. It renders from the same generated tokens the app uses,
 * so it cannot drift: change tokens.json, run tokens:build, and this page moves.
 */

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXXL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  swatchRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  swatch: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: tokens.spacingHorizontalXXXL,
  },
  // Fewer swatches per row, so let them share the width instead of pinning a size.
  swatchGrow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flexGrow: 1,
    flexBasis: '20%',
  },
  chip: {
    height: tokens.spacingVerticalXXXL,
    borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  caption: {
    fontFamily: tokens.fontFamilyNumeric,
  },
  scaleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  scaleBar: {
    height: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorBrandBackground,
  },
  label: {
    minWidth: tokens.spacingHorizontalXXXL,
    fontFamily: tokens.fontFamilyNumeric,
  },
  elevationRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalL,
  },
  elevationTile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: tokens.spacingHorizontalXXXL,
    height: tokens.spacingVerticalXXXL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  componentRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
});

function Section({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  const styles = useStyles();
  return (
    <section className={styles.section}>
      <Title3>{title}</Title3>
      <Text size={200}>{hint}</Text>
      {children}
    </section>
  );
}

export function DesignPage() {
  const styles = useStyles();
  const { scheme } = useColorScheme();

  const brandRungs = Object.entries(rawTokens.brandRamp);
  const typeRamp = Object.entries(rawTokens.fontSize);
  const spacingScale = Object.entries(rawTokens.spacing);
  const radii = Object.entries(rawTokens.radius);
  const elevations = Object.keys(rawTokens.elevation);

  return (
    <div className={styles.page}>
      <div className={styles.section}>
        <Title2>Design system</Title2>
        <Text>
          Every value below is generated from <code>packages/ui/tokens.json</code>. Nothing on this
          page is hand-written, so it stays true as the tokens change. Currently rendering the{' '}
          <strong>{scheme}</strong> theme — use the header toggle to compare.
        </Text>
      </div>

      <Divider />

      <Section title="Brand ramp" hint="Sixteen rungs. Fluent derives its semantic colours from these: 60/70/80 drive light, 90/100/110 drive dark.">
        <div className={styles.swatchRow}>
          {brandRungs.map(([rung, value]) => (
            <div key={rung} className={styles.swatch}>
              <div className={styles.chip} style={{ backgroundColor: value }} />
              <Text size={100} className={styles.caption}>
                {rung}
              </Text>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Compliance states" hint="Severity for the expiration tiers. These are domain tokens, separate from Fluent's generic success and danger colours.">
        <div className={styles.swatchRow}>
          {complianceTones.map((tone) => (
            <div key={tone} className={styles.swatchGrow}>
              <div className={styles.chip} style={{ backgroundColor: complianceTokenFor[tone] }} />
              <Text size={200}>{tone}</Text>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Type ramp" hint="Font sizes, paired with their line heights in the theme.">
        <div className={styles.section}>
          {typeRamp.map(([name, size]) => (
            <div key={name} className={styles.scaleRow}>
              <Text className={styles.label} size={200}>
                {name}
              </Text>
              <Text style={{ fontSize: size }}>Compliance and onboarding</Text>
              <Text size={100} className={styles.caption}>
                {size}
              </Text>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Spacing" hint="One scale, used for both horizontal and vertical rhythm.">
        <div className={styles.section}>
          {spacingScale.map(([name, size]) => (
            <div key={name} className={styles.scaleRow}>
              <Text className={styles.label} size={200}>
                {name}
              </Text>
              <div className={styles.scaleBar} style={{ width: size }} />
              <Text size={100} className={styles.caption}>
                {size}
              </Text>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Radii" hint="Corner rounding.">
        <div className={styles.swatchRow}>
          {radii.map(([name, value]) => (
            <div key={name} className={styles.swatchGrow}>
              <div
                className={styles.chip}
                style={{ backgroundColor: tokens.colorBrandBackground, borderRadius: value }}
              />
              <Text size={100} className={styles.caption}>
                {name} · {value}
              </Text>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Elevation" hint="Shadows live in a separate token file because Figma has no shadow variable type.">
        <div className={styles.elevationRow}>
          {elevations.map((name) => (
            <div key={name} className={styles.swatch}>
              <div
                className={styles.elevationTile}
                style={{ boxShadow: tokens[name as keyof typeof tokens] }}
              />
              <Text size={100} className={styles.caption}>
                {name}
              </Text>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Components" hint="Core Fluent components, rendering through the Vigeo theme.">
        <div className={styles.componentRow}>
          <Button appearance="primary">Send portal invite</Button>
          <Button>Request document</Button>
          <Button appearance="subtle">Cancel</Button>
          <Spinner size="tiny" label="Checking" />
        </div>
        <div className={styles.componentRow}>
          <Badge appearance="tint" color="success">
            Current
          </Badge>
          <Badge appearance="tint" color="warning">
            Expiring
          </Badge>
          <Badge appearance="tint" color="danger">
            Expired
          </Badge>
          <Switch label="Include contract staff" />
        </div>
        <div className={styles.componentRow}>
          <Input placeholder="Search the roster" />
        </div>
        <Card className={styles.card}>
          <Text weight="semibold">Card surface</Text>
          <Text size={200}>
            Used for grouped detail such as a person record or a review-queue item.
          </Text>
        </Card>
      </Section>
    </div>
  );
}
